import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
} from '@mui/material';
import {
  LocalOffer as CouponIcon,
  WhatsApp as WhatsAppIcon,
  Call as CallIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarMonthIcon,
  DateRange as DateRangeIcon,
  CheckCircle as CheckCircleIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const cleanPhoneDigits = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
};

/**
 * @param {'all'|'issued'|'used'} variant
 *  - all: full issued list with status filter (standalone page)
 *  - issued: unused (active) coupons with Call + WhatsApp follow-up
 *  - used: redeemed coupons list
 */
const IssuedCoupons = ({ variant = 'all', embedded = false } = {}) => {
  const lockedStatus = variant === 'issued' ? 'active' : variant === 'used' ? 'used' : null;
  const showStats = variant === 'used' || variant === 'all';

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(lockedStatus || 'all');
  const [sortBy, setSortBy] = useState(variant === 'used' ? 'used_date' : 'assigned_date');
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  
  // Statistics state
  const [stats, setStats] = useState({
    todayUsed: 0,
    highestDay: { date: null, total: 0 },
    monthTotal: 0,
    customDateTotal: null,
    totalUsedService: 0,
    totalCouponsBill: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const effectiveStatus = lockedStatus || (statusFilter !== 'all' ? statusFilter : '');

  const fetchIssuedCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/coupons/issued`, {
        params: {
          page,
          limit,
          search,
          status: effectiveStatus,
          sort_by: sortBy,
          sort_dir: sortBy === 'expired_date' ? 'asc' : 'desc',
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setCoupons(response.data.coupons || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotal(response.data.pagination?.total || 0);
      } else {
        setError('Failed to fetch issued coupons');
      }
    } catch (err) {
      console.error('Error fetching issued coupons:', err);
      setError(err.response?.data?.message || 'Failed to fetch issued coupons');
      toast.error('Failed to load issued coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      
      const response = await axios.get(`${API_BASE_URL}/coupons/issued/stats`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchIssuedCoupons();
  }, [page, search, statusFilter, sortBy]);

  // Reset selection when search/filter changes (but keep selection when just page changes)
  useEffect(() => {
    setSelectedCoupons(new Set());
    setSelectAll(false);
  }, [search, statusFilter, sortBy]);

  useEffect(() => {
    if (showStats) {
      fetchStatistics();
    }
  }, [customStartDate, customEndDate, showStats]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1); // Reset to first page on filter change
  };

  const handleCallCustomer = (coupon) => {
    const phone = coupon.customer_mobile || coupon.customer_phone || '';
    const cleanPhone = cleanPhoneDigits(phone);
    if (cleanPhone.length !== 10) {
      toast.error('Customer phone number not available');
      return;
    }
    window.location.href = `tel:+91${cleanPhone}`;
  };

  const handleWhatsAppShare = (coupon) => {
    const customerName = coupon.customer_name || 'Customer';
    const couponName = coupon.coupon_name || coupon.name || 'Coupon';
    const couponCode = coupon.coupon_code || coupon.code || '';
    const offerPrice = parseFloat(coupon.final_price || coupon.offer_price || 0) || 0;
    const validUntil = coupon.expired_date || coupon.valid_until;
    
    const message = `Hi ${customerName}! 🎁\n\nYou have a coupon: *${couponName}*\nCode: *${couponCode}*\n\nSpecial Price: ₹${offerPrice.toFixed(2)}\nValid until: ${validUntil ? format(new Date(validUntil), 'dd/MM/yyyy') : 'N/A'}\n\nVisit us to use this coupon!\n\nThank you! 🙏`;
    
    const phone = coupon.customer_mobile || coupon.customer_phone || '';
    const cleanPhone = cleanPhoneDigits(phone);
    if (cleanPhone.length === 10) {
      const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } else {
      toast.error(phone ? 'Invalid phone number for WhatsApp share' : 'Customer phone number not available');
    }
  };

  const handleSelectCoupon = (couponId) => {
    // Ensure ID is stored as string for consistency
    const idString = String(couponId);
    const newSelected = new Set(selectedCoupons);
    if (newSelected.has(idString)) {
      newSelected.delete(idString);
    } else {
      newSelected.add(idString);
    }
    setSelectedCoupons(newSelected);
    setSelectAll(newSelected.size === coupons.length && coupons.length > 0);
    console.log('Selected coupons after toggle:', Array.from(newSelected));
  };

  const fetchAllMatchingCouponIds = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all matching coupons without pagination to get all IDs
      const response = await axios.get(`${API_BASE_URL}/coupons/issued`, {
        params: {
          page: 1,
          limit: 10000, // Large limit to get all matching records
          search,
          status: effectiveStatus,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const allMatchingCoupons = response.data.coupons || [];
        const ids = allMatchingCoupons.map(coupon => {
          const id = coupon.id || coupon.coupon_id;
          if (!id) {
            console.warn('Coupon missing ID:', coupon);
            return null;
          }
          // Convert to string for consistent Set storage
          return String(id);
        }).filter(id => id !== null && id !== 'undefined' && id !== 'null');
        
        console.log(`Fetched ${ids.length} matching coupon IDs:`, ids);
        console.log('Sample coupon data:', allMatchingCoupons.slice(0, 2));
        return ids;
      }
      return [];
    } catch (err) {
      console.error('Error fetching all matching coupon IDs:', err);
      toast.error('Failed to fetch all matching coupons');
      return [];
    }
  };

  const handleSelectAll = async () => {
    if (selectAll) {
      // Deselect all
      setSelectedCoupons(new Set());
      setSelectAll(false);
    } else {
      // Select all matching coupons (across all pages)
      const allMatchingIds = await fetchAllMatchingCouponIds();
      if (allMatchingIds.length > 0) {
        setSelectedCoupons(new Set(allMatchingIds));
        setSelectAll(true);
        toast.info(`Selected ${allMatchingIds.length} coupon(s) matching your search/filter`);
      } else {
        toast.warning('No coupons found to select');
      }
    }
  };

  const handleDeleteClick = (coupon) => {
    if (coupon) {
      // Single delete
      setCouponToDelete(coupon);
      setDeleteDialogOpen(true);
    } else if (selectedCoupons.size > 0) {
      // Bulk delete
      setCouponToDelete(null);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      let idsToDelete = [];

      if (couponToDelete) {
        // Single delete
        idsToDelete = [couponToDelete.id || couponToDelete.coupon_id];
      } else {
        // Bulk delete - get IDs from selectedCoupons Set
        idsToDelete = Array.from(selectedCoupons);
        console.log('Bulk delete - Selected coupons Set:', selectedCoupons);
        console.log('Bulk delete - IDs to delete:', idsToDelete);
      }

      if (idsToDelete.length === 0) {
        toast.error('No coupons selected for deletion');
        console.warn('No IDs to delete. Selected coupons:', selectedCoupons);
        setDeleting(false);
        return;
      }

      // Ensure all IDs are valid (not null/undefined) and convert to string for API
      const validIds = idsToDelete
        .map(id => {
          // Handle both number and string IDs
          if (id === null || id === undefined) {
            return null;
          }
          const idValue = String(id).trim();
          return idValue && idValue !== 'undefined' && idValue !== 'null' && idValue !== '' ? idValue : null;
        })
        .filter(id => id !== null);

      if (validIds.length === 0) {
        toast.error('No valid coupon IDs found for deletion');
        console.error('Invalid IDs received:', idsToDelete);
        setDeleting(false);
        return;
      }

      console.log(`Deleting ${validIds.length} coupons (from ${idsToDelete.length} selected) with IDs:`, validIds);
      console.log('Selected coupons Set:', Array.from(selectedCoupons));

      // Delete all selected coupons sequentially to avoid race conditions
      let successful = 0;
      let failed = 0;
      const errors = [];

      for (let i = 0; i < validIds.length; i++) {
        const id = validIds[i];
        try {
          console.log(`Deleting coupon ${i + 1}/${validIds.length}: ID ${id}`);
          const response = await axios.delete(`${API_BASE_URL}/coupons/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          console.log(`Response for ID ${id}:`, response.data);
          
          if (response.data && response.data.success) {
            successful++;
            console.log(`✓ Successfully deleted coupon ID ${id}`);
          } else {
            failed++;
            const errorMsg = response.data?.message || 'Unknown error';
            errors.push(`ID ${id}: ${errorMsg}`);
            console.warn(`✗ Failed to delete coupon ID ${id}:`, errorMsg);
          }
        } catch (err) {
          failed++;
          const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
          errors.push(`ID ${id}: ${errorMsg}`);
          console.error(`✗ Error deleting coupon ID ${id}:`, err);
          console.error('Error details:', {
            status: err.response?.status,
            data: err.response?.data,
            message: err.message
          });
        }
        
        // Small delay between deletions to avoid overwhelming the server
        if (i < validIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`Deletion complete: ${successful} successful, ${failed} failed`);
      
      if (successful > 0) {
        // Show alert with count
        const message = `${successful} coupon(s) deleted successfully${failed > 0 ? `, ${failed} failed` : ''}`;
        toast.success(message, {
          autoClose: 5000,
        });
        
        // Show browser alert with detailed count
        window.alert(`✅ Success!\n\n${successful} coupon(s) deleted successfully${failed > 0 ? `\n${failed} coupon(s) failed to delete` : ''}`);
        
        if (failed > 0 && errors.length > 0) {
          console.error('Delete errors:', errors);
          toast.warning(`Some deletions failed. Check console for details.`);
        }
        setDeleteDialogOpen(false);
        setCouponToDelete(null);
        setSelectedCoupons(new Set());
        setSelectAll(false);
        // Refresh the coupons list
        await fetchIssuedCoupons();
        // Refresh statistics
        await fetchStatistics();
      } else {
        const errorMessage = `Failed to delete all coupons. Errors: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`;
        toast.error(errorMessage);
        window.alert(`❌ Error!\n\nFailed to delete coupons.\n\nErrors: ${errors.slice(0, 5).join('\n')}${errors.length > 5 ? '\n...' : ''}`);
        console.error('All deletions failed. Full error list:', errors);
      }
    } catch (err) {
      console.error('Error deleting coupons:', err);
      toast.error(err.response?.data?.message || 'Failed to delete coupons');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCouponToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'used':
        return 'default';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const pageTitle = variant === 'used' ? 'Used Coupons' : 'Issued Coupons';
  const pageSubtitle =
    variant === 'used'
      ? 'Coupons that customers have already redeemed'
      : variant === 'issued'
        ? 'Unused coupons issued to customers — call or WhatsApp to help them redeem'
        : 'View and manage coupons issued to customers';

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              {!embedded && (
                <Typography variant="h4" component="h1" gutterBottom>
                  {pageTitle}
                </Typography>
              )}
              <Typography variant={embedded ? 'subtitle1' : 'body2'} color="text.secondary" fontWeight={embedded ? 600 : 400}>
                {pageSubtitle}
              </Typography>
            </Box>
          </Box>

          {/* Statistics Cards — useful mainly for used / all views */}
          {showStats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Today Used Coupons */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Today Used
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : stats.todayUsed}
                      </Typography>
                    </Box>
                    <TodayIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Highest Day */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Highest Day
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : stats.highestDay.total}
                      </Typography>
                      {stats.highestDay.date && (
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {format(new Date(stats.highestDay.date), 'dd/MM/yyyy')}
                        </Typography>
                      )}
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Month Total */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        This Month
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : stats.monthTotal}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {format(new Date(), 'MMMM yyyy')}
                      </Typography>
                    </Box>
                    <CalendarMonthIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Custom Date Range */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Custom Range
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : (stats.customDateTotal !== null ? stats.customDateTotal : '-')}
                      </Typography>
                      {customStartDate && customEndDate && (
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {format(new Date(customStartDate), 'dd/MM')} - {format(new Date(customEndDate), 'dd/MM')}
                        </Typography>
                      )}
                    </Box>
                    <DateRangeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Used Service */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.dark', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Used Service
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : stats.totalUsedService}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        All time used coupons
                      </Typography>
                    </Box>
                    <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Coupons Bill */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Coupons Bill
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {statsLoading ? <CircularProgress size={24} color="inherit" /> : `₹${stats.totalCouponsBill.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Total revenue from coupons
                      </Typography>
                    </Box>
                    <CurrencyRupeeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          )}

          {/* Custom Date Range Picker */}
          {showStats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          )}

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={lockedStatus ? 7 : 5}>
              <TextField
                fullWidth
                placeholder="Search customer, phone, coupon, or shop name..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            {!lockedStatus && (
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="used">Used</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            )}
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  {variant === 'used' ? (
                    <MenuItem value="used_date">Used date</MenuItem>
                  ) : (
                    <MenuItem value="assigned_date">Assigned date</MenuItem>
                  )}
                  <MenuItem value="expired_date">Expiry date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  Total: <strong>{total}</strong> coupons
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Selection Bar - Visible when items are selected - Right before table */}
          {variant !== 'used' && selectedCoupons.size > 0 && (
            <Box 
              sx={{ 
                mb: 2,
                p: 2.5,
                bgcolor: 'error.light',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: 3,
                border: '2px solid',
                borderColor: 'error.main',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DeleteIcon sx={{ color: 'white', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {selectedCoupons.size} Coupon(s) Selected
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(null)}
                disabled={deleting}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  bgcolor: 'white',
                  color: 'error.main',
                  minWidth: 200,
                  '&:hover': {
                    bgcolor: 'error.dark',
                    color: 'white',
                    transform: 'scale(1.05)'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    color: 'rgba(0, 0, 0, 0.5)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {deleting ? 'Deleting...' : `Delete ${selectedCoupons.size} Selected`}
              </Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : coupons.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CouponIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {variant === 'used' ? 'No used coupons found' : 'No issued coupons found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {search || (!lockedStatus && statusFilter !== 'all')
                  ? 'Try adjusting your filters'
                  : variant === 'used'
                    ? 'Redeemed coupons will appear here'
                    : 'Coupons will appear here once they are issued to customers'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      {variant !== 'used' && (
                      <TableCell padding="checkbox">
                        <Tooltip title={selectAll ? "Deselect all matching coupons" : "Select all matching coupons (across all pages)"}>
                          <Checkbox
                            checked={selectAll}
                            indeterminate={selectedCoupons.size > 0 && selectedCoupons.size < total}
                            onChange={handleSelectAll}
                            color="primary"
                          />
                        </Tooltip>
                      </TableCell>
                      )}
                      <TableCell><strong>Customer Mobile</strong></TableCell>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Profession</strong></TableCell>
                      <TableCell><strong>Coupon Name</strong></TableCell>
                      <TableCell><strong>Shop / Mitra</strong></TableCell>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Issued Date & Time</strong></TableCell>
                      {variant === 'used' && (
                        <TableCell><strong>Used Date & Time</strong></TableCell>
                      )}
                      <TableCell><strong>Expired Date</strong></TableCell>
                      {variant !== 'used' && (
                        <TableCell><strong>Scratched</strong></TableCell>
                      )}
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coupons.map((coupon) => {
                      const couponId = coupon.id || coupon.coupon_id;
                      const idString = String(couponId);
                      const isSelected = selectedCoupons.has(idString);
                      const isExpired = coupon.status === 'expired' || (coupon.expired_date || coupon.valid_until ? new Date(coupon.expired_date || coupon.valid_until) < new Date() : false);
                      const isUsed = coupon.status === 'used';
                      const isDisabled = isExpired || isUsed;
                      const hasPhone = cleanPhoneDigits(coupon.customer_mobile || coupon.customer_phone).length === 10;
                      return (
                      <TableRow 
                        key={couponId} 
                        hover={variant === 'used' || !isDisabled} 
                        selected={isSelected}
                        sx={{
                          opacity: variant !== 'used' && isDisabled ? 0.6 : 1,
                          backgroundColor: variant !== 'used' && isDisabled ? 'action.disabledBackground' : 'inherit',
                          '&:hover': {
                            backgroundColor: variant !== 'used' && isDisabled ? 'action.disabledBackground' : undefined
                          }
                        }}
                      >
                        {variant !== 'used' && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectCoupon(couponId)}
                            color="primary"
                            disabled={isDisabled}
                          />
                        </TableCell>
                        )}
                        <TableCell>{coupon.customer_mobile || coupon.customer_phone || 'N/A'}</TableCell>
                        <TableCell>{coupon.customer_name || 'N/A'}</TableCell>
                        <TableCell>{coupon.customer_profession || 'N/A'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CouponIcon fontSize="small" color="primary" />
                            {coupon.coupon_name || coupon.name || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {coupon.mitra_name || coupon.shop_name || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={coupon.coupon_code || coupon.code || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {coupon.issued_date || coupon.issued_at || coupon.generated_at
                            ? format(
                                new Date(coupon.issued_date || coupon.issued_at || coupon.generated_at),
                                'dd/MM/yyyy hh:mm a',
                              )
                            : 'N/A'}
                        </TableCell>
                        {variant === 'used' && (
                          <TableCell>
                            {coupon.used_at
                              ? format(new Date(coupon.used_at), 'dd/MM/yyyy hh:mm a')
                              : 'N/A'}
                          </TableCell>
                        )}
                        <TableCell>
                          {coupon.expired_date || coupon.valid_until
                            ? format(new Date(coupon.expired_date || coupon.valid_until), 'dd/MM/yyyy')
                            : 'N/A'}
                        </TableCell>
                        {variant !== 'used' && (
                        <TableCell>
                          {coupon.scratched_at
                            ? format(new Date(coupon.scratched_at), 'dd/MM/yyyy hh:mm a')
                            : '—'}
                        </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={
                              isExpired && coupon.status !== 'expired' ? 'expired' :
                              isUsed ? 'used' :
                              coupon.status || 'active'
                            }
                            color={getStatusColor(
                              isExpired && coupon.status !== 'expired' ? 'expired' :
                              isUsed ? 'used' :
                              coupon.status
                            )}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(variant === 'issued' || variant === 'all' || variant === 'used') && (
                              <Tooltip title={hasPhone ? 'Call customer' : 'Phone not available'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCallCustomer(coupon)}
                                    disabled={
                                      !hasPhone ||
                                      (variant === 'all' && isDisabled)
                                    }
                                  >
                                    <CallIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {(variant === 'issued' || variant === 'all') && (
                              <Tooltip title={
                                variant === 'issued'
                                  ? (hasPhone ? 'WhatsApp customer to redeem coupon' : 'Phone not available')
                                  : (isDisabled ? (isUsed ? 'Coupon is already used' : 'Coupon has expired') : 'Share via WhatsApp')
                              }>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleWhatsAppShare(coupon)}
                                    disabled={
                                      !hasPhone ||
                                      (variant === 'issued' ? isUsed : isDisabled)
                                    }
                                  >
                                    <WhatsAppIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {/* Hide individual delete on used tab; disable for used/expired on issued */}
                            {variant !== 'used' && selectedCoupons.size === 0 && (
                              <Tooltip title={isDisabled ? (isUsed ? 'Cannot delete used coupon' : 'Cannot delete expired coupon') : "Delete Coupon"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(coupon)}
                                    disabled={isDisabled}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {couponToDelete ? 'Delete Coupon?' : `Delete ${selectedCoupons.size} Coupon(s)?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {couponToDelete ? (
              <>
                Are you sure you want to delete this coupon?
                <br />
                <br />
                <strong>Coupon:</strong> {couponToDelete?.coupon_name || couponToDelete?.name || 'N/A'}
                <br />
                <strong>Code:</strong> {couponToDelete?.coupon_code || couponToDelete?.code || 'N/A'}
                <br />
                <strong>Customer:</strong> {couponToDelete?.customer_name || 'N/A'}
                <br />
                <br />
                <strong>⚠️ This action cannot be undone. The coupon will be marked as inactive.</strong>
              </>
            ) : (
              <>
                Are you sure you want to delete <strong style={{ fontSize: '1.2em', color: '#d32f2f' }}>{selectedCoupons.size}</strong> selected coupon(s)?
                <br />
                <br />
                <strong>⚠️ This action cannot be undone. All {selectedCoupons.size} selected coupons will be marked as inactive.</strong>
                <br />
                <br />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  After deletion, you will see an alert showing exactly how many records were deleted.
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IssuedCoupons;

